import React, { useState, useEffect } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  Menu as MenuIcon,
} from "@mui/icons-material";

// Icons
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BankIcon from "@mui/icons-material/AccountBalance";
import WalletIcon from "@mui/icons-material/AccountBalanceWallet";
import FileIcon from "@mui/icons-material/Description";
import StoreIcon from "@mui/icons-material/Store";
import UsersIcon from "@mui/icons-material/Group";
import TruckIcon from "@mui/icons-material/LocalShipping";
import ArchiveBoxIcon from "@mui/icons-material/Inventory";
import ReportIcon from "@mui/icons-material/Assessment";
import TextSnippetIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";

const InvoiceSideNav = ({ forceCollapsed = false }) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [activeMenu, setActiveMenu] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  
  const selectedCompanyInfo = useSelector(
    (state) => state.kyc?.selectedCompanyInfo
  );

  const invoiceTheme = {
    primaryRed: "#B71C1C",
    backgroundGray: "#F5F5F5",
    textColor: "#333333",
  };

  useEffect(() => {
    if (forceCollapsed) {
      setCollapsed(true);
    } else if (isSmallScreen || isMobile) {
      setCollapsed(true);
    } else if (isTablet) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [forceCollapsed, isSmallScreen, isMobile, isTablet]);

  const handleDrawerToggle = () => {
    if (!isMobile && !forceCollapsed) {
      setCollapsed(!collapsed);
    }
  };

  const handleMenuClick = (index) => {
    if (!collapsed) {
      setActiveMenu(activeMenu === index ? null : index);
    }
  };

  const drawerWidth = collapsed ? 72 : 280;

  // Check if current route matches any of the menu items
  const isActiveRoute = (paths) => {
    return paths.some(path => location.pathname === path);
  };

  useEffect(() => {
    // Auto-expand menus based on current route
    if (isActiveRoute(["/CustomerInvoiceProfile"])) {
      setActiveMenu(1);
    } else if (isActiveRoute(["/CustomerInvoice", "/quotations", "/sale-returns", "/payments-in", "/delivery-challans"])) {
      setActiveMenu(2);
    } else if (isActiveRoute(["/InvoicePurchaseHistory", "/purchase-return", "/purchase-order", "/payment-out"])) {
      setActiveMenu(3);
    }
  }, [location.pathname]);

  const menuItems = [
    {
      id: 1,
      title: "Profile",
      icon: <PersonIcon />,
      items: [
        { name: "My Profile", path: "/CustomerInvoiceProfile" },
      ],
    },
    {
      id: 2,
      title: "Sales",
      icon: <TrendingUpIcon />,
      items: [
        { name: "Invoices", path: "/CustomerInvoice" },
        { name: "Quotations", path: "/quotations" },
        { name: "Sale Returns", path: "/sale-returns" },
        { name: "Payments In", path: "/payments-in" },
        { name: "Delivery Challans", path: "/delivery-challans" },
      ],
    },
    {
      id: 3,
      title: "Purchases",
      icon: <TrendingDownIcon />,
      items: [
        { name: "Purchase", path: "/InvoicePurchaseHistory" },
        { name: "Purchase Return", path: "/purchase-return" },
        { name: "Purchase Order", path: "/purchase-order" },
        { name: "Payment Out", path: "/payment-out" },
      ],
    },
  ];

  const singleItems = [
    { title: "Dashboard", icon: <HomeIcon />, path: "/Dashboard" },
    { title: "New Invoice", icon: <TextSnippetIcon />, path: "/invoicemanagement" },
    { title: "Expense", icon: <WalletIcon />, path: "/expense" },
    { title: "Banking", icon: <BankIcon />, path: "/banking" },
    { title: "Cash", icon: <AttachMoneyIcon />, path: "/cash" },
    { title: "Documents", icon: <FileIcon />, path: "/invoiceDocuments" },
    { title: "Product/Services", icon: <StoreIcon />, path: "/InvoiceProducts" },
    { title: "Customer", icon: <UsersIcon />, path: "/InvoiceCustomers" },
    { title: "Vendor", icon: <TruckIcon />, path: "/Vanders" },
    { title: "Reports", icon: <ReportIcon />, path: "/InvoiceReports" },
    { title: "Inventory", icon: <ArchiveBoxIcon />, path: "/InventoryManagement" },
  ];

  const renderMenuItem = (item) => (
    <Box key={item.id}>
      <ListItemButton
        onClick={() => handleMenuClick(item.id)}
        selected={activeMenu === item.id}
        sx={{
          minHeight: 48,
          px: collapsed ? 1.5 : 2.5,
          margin: "4px 16px",
          borderRadius: "8px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: "#FFF6E9",
            transform: "translateX(4px)",
            "& .MuiListItemText-primary": {
              color: invoiceTheme.primaryRed,
              fontWeight: "bold",
            },
            "& .MuiListItemIcon-root": {
              color: invoiceTheme.primaryRed,
            },
          },
          "&.Mui-selected": {
            backgroundColor: "#FFF6E9",
            "& .MuiListItemText-primary": {
              color: invoiceTheme.primaryRed,
              fontWeight: "bold",
            },
            "& .MuiListItemIcon-root": {
              color: invoiceTheme.primaryRed,
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: collapsed ? 0 : 3,
            justifyContent: "center",
            color: theme.palette.primary.main,
          }}
        >
          <Tooltip
            title={item.title}
            placement="right"
            disableHoverListener={!collapsed}
          >
            {item.icon}
          </Tooltip>
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText
              primary={item.title}
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                },
              }}
            />
            {activeMenu === item.id ? (
              <KeyboardArrowRight />
            ) : (
              <KeyboardArrowDown />
            )}
          </>
        )}
      </ListItemButton>

      {!collapsed && (
        <Collapse in={activeMenu === item.id} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.items.map((subItem, index) => (
              <ListItemButton
                key={index}
                component={NavLink}
                to={subItem.path}
                sx={{
                  pl: 4,
                  margin: "2px 16px",
                  borderRadius: "8px",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#FFF6E9",
                    transform: "translateX(4px)",
                    "& .MuiListItemText-primary": {
                      color: invoiceTheme.primaryRed,
                      fontWeight: "bold",
                    },
                  },
                  "&.active": {
                    backgroundColor: "#FFF6E9",
                    "& .MuiListItemText-primary": {
                      color: invoiceTheme.primaryRed,
                      fontWeight: "bold",
                    },
                  },
                }}
              >
                <ListItemText
                  primary={subItem.name}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.8rem",
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );

  const renderSingleItem = (item) => (
    <ListItemButton
      key={item.title}
      component={NavLink}
      to={item.path}
      sx={{
        minHeight: 48,
        px: collapsed ? 1.5 : 2.5,
        margin: "4px 16px",
        borderRadius: "8px",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: "#FFF6E9",
          transform: "translateX(4px)",
          "& .MuiListItemText-primary": {
            color: invoiceTheme.primaryRed,
            fontWeight: "bold",
          },
          "& .MuiListItemIcon-root": {
            color: invoiceTheme.primaryRed,
          },
        },
        "&.active": {
          backgroundColor: "#FFF6E9",
          "& .MuiListItemText-primary": {
            color: invoiceTheme.primaryRed,
            fontWeight: "bold",
          },
          "& .MuiListItemIcon-root": {
            color: invoiceTheme.primaryRed,
          },
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 3,
          justifyContent: "center",
          color: theme.palette.primary.main,
        }}
      >
        <Tooltip
          title={item.title}
          placement="right"
          disableHoverListener={!collapsed}
        >
          {item.icon}
        </Tooltip>
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.title}
          sx={{
            "& .MuiListItemText-primary": {
              fontSize: "0.875rem",
              fontWeight: 500,
            },
          }}
        />
      )}
    </ListItemButton>
  );

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          justifyContent: collapsed ? "center" : "flex-start",
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: collapsed ? 64 : 120,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            width: "100%",
            mb: collapsed ? 0 : 2,
          }}
        >
          <Link
            to="/Dashboard"
            style={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
            
              {!collapsed && (
                <>
                  <Typography
                    variant="h6"
                    component="span"
                    sx={{ 
                      color: "black", 
                      fontSize: "1.5rem", 
                      fontWeight: "bold",
                      mr: 0.5,
                    }}
                  >
                    Invoice
                  </Typography>
                  <Typography
                    variant="h6"
                    component="span"
                    sx={{ 
                      color: invoiceTheme.primaryRed, 
                      fontSize: "1.5rem", 
                      fontWeight: "bold" 
                    }}
                  >
                    Portal
                  </Typography>
                </>
              )}
            </Box>
          </Link>
          {!isMobile && !forceCollapsed && (
            <IconButton onClick={handleDrawerToggle} size="small">
              <MenuIcon />
            </IconButton>
          )}
        </Box>

        {!collapsed && (
          <>
            <Typography
              variant="body1"
              sx={{
                color: invoiceTheme.textColor,
                fontSize: "0.75rem",
                mb: 2,
                textAlign: "center",
              }}
            >
              {selectedCompanyInfo?.legalName || "Your Company Name"}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{
                color: invoiceTheme.primaryRed,
                borderColor: invoiceTheme.primaryRed,
                fontSize: "0.75rem",
                "&:hover": {
                  borderColor: invoiceTheme.primaryRed,
                  backgroundColor: `${invoiceTheme.primaryRed}10`,
                },
              }}
            >
              Switch Profile
            </Button>
          </>
        )}
      </Box>

      {/* Menu Items */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          py: 1,
        }}
      >
        <List>
          {/* Single items first */}
          {singleItems.slice(0, 2).map(renderSingleItem)}
          
          {/* Menu items with dropdowns */}
          {menuItems.map(renderMenuItem)}
          
          {/* Remaining single items */}
          {singleItems.slice(2).map(renderSingleItem)}
        </List>
      </Box>

      {/* Footer Logo */}
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          minHeight: {
            xs: 120,
            sm: 140,
            md: collapsed ? 100 : 160,
            lg: 180,
          },
          flexShrink: 0,
          backgroundColor: "#fafafa",
        }}
      >
        <Box
          component="img"
          src="/icon.jpg"
          alt="Company Logo"
          sx={{
            height: {
              xs: collapsed ? 40 : 60,
              sm: collapsed ? 50 : 70,
              md: collapsed ? 60 : 90,
              lg: collapsed ? 70 : 120,
            },
            width: "auto",
            objectFit: "contain",
            maxWidth: {
              xs: collapsed ? "33px" : "148px",
              sm: collapsed ? "38px" : "178px",
              md: collapsed ? "43px" : "198px",
              lg: collapsed ? "48px" : "218px",
            },
            display: "block",
            mt: { xs: 0.5, sm: 1 },
          }}
        />
      </Box>
    </Box>
  );

  return (
    <motion.div
      animate={{ width: drawerWidth }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{
        width: drawerWidth,
        flexShrink: 0,
        height: "100%",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: drawerWidth,
          height: "100%",
          borderRight: "1px solid #e0e0e0",
          backgroundColor: "#fff",
          overflow: "hidden",
          position: "fixed",
          top: 64,
          left: 0,
          bottom: 0,
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: !collapsed ? "3px" : "0px",
            backgroundColor: invoiceTheme.primaryRed,
            transition: "width 0.3s ease-in-out",
            zIndex: 1,
          },
        }}
      >
        {drawer}
      </Box>
    </motion.div>
  );
};

export default InvoiceSideNav;
